/**
 * Date utilities — handles backend datetime strings that may lack timezone info.
 *
 * Backend stores `datetime.utcnow()` and serializes as ISO without 'Z' suffix
 * (e.g. "2026-04-27T20:06:41.767000"). JavaScript would interpret this as local time,
 * causing 1-2h offset bugs or "Invalid Date" when combined with timezone math.
 *
 * Always use `parseUTC()` instead of `new Date()` for backend-provided dates.
 */

/**
 * Parse an ISO datetime string, treating it as UTC if no timezone is specified.
 * Returns a Date object (potentially invalid) — drop-in replacement for `new Date(value)`.
 * @param {string|Date|number|null|undefined} value
 * @returns {Date}
 */
export function parseUTC(value) {
  if (value == null || value === '') return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value !== 'string') return new Date(NaN);

  // If string already has timezone info (Z, +HH:MM, -HH:MM), parse as-is
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  return new Date(hasTz ? value : `${value}Z`);
}

/** Helper: returns true if the value yields a valid date */
export function isValidDate(value) {
  const d = parseUTC(value);
  return !isNaN(d.getTime());
}

/** Format date as DD/MM/YYYY in local timezone */
export function formatDate(value) {
  const d = parseUTC(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
}

/** Format time as HH:MM in local timezone */
export function formatTime(value) {
  const d = parseUTC(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** Format full date+time as DD/MM/YYYY HH:MM in local timezone */
export function formatDateTime(value) {
  const d = parseUTC(value);
  return isNaN(d.getTime())
    ? '—'
    : `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}
